"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FinalCallToAction() {
  return <section id="final-call-to-action" className="relative py-20 md:py-32 bg-dark-background text-foreground-dark overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-dark-background to-dark-background" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start max-w-7xl mx-auto">
          <CTAContent />
          <ContactFormWrapper />
        </div>
      </div>
    </section>;
}
function CTAContent() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const {
    ref: copyRef,
    isVisible: copyVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const {
    ref: buttonRef,
    isVisible: buttonVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  return <div className="lg:sticky lg:top-24 space-y-8">
      <div ref={headlineRef} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-tight">
          Let's Create Something{" "}
          <span className="text-accent-dark">Powerful</span>
        </h2>
      </div>

      <div ref={copyRef} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-100 ${copyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <p className="text-lg leading-relaxed max-w-xl !text-[hsl(210,14%,83%)] md:text-xl">
          Ready to elevate your brand with cinematic video and premium
          photography? Fill out the form and we'll be in touch within 24 hours
          to discuss your project.
        </p>
      </div>

      <div ref={buttonRef} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-200 ${buttonVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <Button size="lg" className="bg-accent text-foreground hover:bg-accent-hover hover:-translate-y-1 hover:shadow-strong transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] px-8 py-6 text-base font-semibold">
          Get Started Now
        </Button>
      </div>
    </div>;
}
function ContactFormWrapper() {
  return <FormProvider className="glass-effect p-8 md:p-10" formId="final_cta_form_home" sectionName="final-call-to-action" defaultValues={{
    name: "",
    email: "",
    phone: "",
    service_needed: "",
    budget_range: "",
    message: ""
  }} validationRules={{
    name: {
      required: true
    },
    email: {
      pattern: "email"
    },
    phone: {
      required: true,
      pattern: "phone"
    },
    service_needed: {
      required: true
    },
    message: {
      required: true
    }
  }} submitText="Send My Inquiry" successMessage="Thank you! We'll contact you within 24 hours to discuss your project.">
      <ContactFormContent />
    </FormProvider>;
}
function ContactFormContent() {
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className={`p-8 bg-accent/10 border border-accent/30 rounded-lg transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="text-center space-y-4">
          <div className="size-16 mx-auto bg-accent rounded-full flex items-center justify-center">
            <svg className="size-8 text-accent-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-foreground text-lg font-medium">
            {successMessage}
          </p>
          <Button onClick={resetForm} variant="outline" className="mt-6 border-accent text-foreground hover:bg-accent-light hover:text-foreground">
            Send Another Inquiry
          </Button>
        </div>
      </div>;
  }
  return <div ref={formRef} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
      <Form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <FormField control={control} name="name" rules={{
          required: "Name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">
                  Your Name
                </FormLabel>
                <FormControl>
                  <Input placeholder="Muhammad Ali" {...field} className="bg-background/50 border-border focus-visible:border-accent text-foreground placeholder:text-muted-foreground transition-all duration-[var(--duration-fast)]" />
                </FormControl>
                <FormDescription className="text-foreground/70">
                  First and last name
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">
                  Email Address (optional)
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="Mohammed@example.com" {...field} className="bg-background/50 border-border focus-visible:border-accent text-foreground placeholder:text-muted-foreground transition-all duration-[var(--duration-fast)]" />
                </FormControl>
                <FormDescription className="text-foreground/70">
                  Optional - we can reach you via phone/WhatsApp
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Phone number is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">
                  Phone / WhatsApp
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+966 5..." {...field} className="bg-background/50 border-border focus-visible:border-accent text-foreground placeholder:text-muted-foreground transition-all duration-[var(--duration-fast)]" />
                </FormControl>
                <FormDescription className="text-foreground/70">
                  We'll contact you via WhatsApp or call
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="service_needed" rules={{
          required: "Please select a service"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">
                  Service Needed
                </FormLabel>
                <FormControl>
                  <select {...field} className="flex h-9 w-full rounded-md border border-border bg-background/50 px-3 py-1 text-base shadow-xs transition-all outline-none focus-visible:border-accent focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 text-foreground">
                    <option value="">Select a service</option>
                    <option value="Video Production">Video Production</option>
                    <option value="Photography">Photography</option>
                    <option value="Event Coverage">Event Coverage</option>
                    <option value="Social Media Content">
                      Social Media Content
                    </option>
                    <option value="Post-Production">Post-Production</option>
                    <option value="Other">Other</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="budget_range" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">
                  Budget Range (optional)
                </FormLabel>
                <FormControl>
                  <select {...field} className="flex h-9 w-full rounded-md border border-border bg-background/50 px-3 py-1 text-base shadow-xs transition-all outline-none focus-visible:border-accent focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 text-foreground">
                    <option value="">Select budget (optional)</option>
                    <option value="SAR 5,000 or less">SAR 5,000 or less</option>
                    <option value="SAR 5,000 – SAR 10,000">SAR 5,000 – SAR 10,000</option>
                    <option value="SAR 10,000 – SAR 20,000">SAR 10,000 – SAR 20,000</option>
                    <option value="SAR 20,000+">SAR 20,000+</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="message" rules={{
          required: "Project details are required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">
                  Project Details
                </FormLabel>
                <FormControl>
                  <Textarea placeholder="Briefly describe your project, timeline, and any special requirements..." {...field} className="bg-background/50 border-border focus-visible:border-accent text-foreground placeholder:text-muted-foreground transition-all duration-[var(--duration-fast)] min-h-32" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm">{submitError}</p>
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-foreground hover:bg-accent-hover hover:-translate-y-0.5 hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] py-6 text-base font-semibold">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Sending Your Inquiry...
              </> : "Send My Inquiry"}
          </Button>
        </div>
      </Form>
    </div>;
}